
// ==== Reusable hook + helpers for table images ====
// (Place in components/Imageshelper.ts next to your existing exports)

import React, { useEffect, useMemo, useState } from "react";

// If these are in this file already, you can remove these imports and use local defs.
import { batchPresignUrls, collectCloudIds } from "./Imageshelper";

// 24-hex Cloud Drive id?
const is24Hex = (s?: string) => !!s && /^[0-9a-f]{24}$/i.test(s);

// Build legacy URL when the row has old "uploads/..." relative path
// export function buildLegacyUrl(raw?: string): string | undefined {
//   if (typeof raw === "string" && raw.includes("uploads/")) {
//     const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
//     return `${base}/${raw.replace(/^\//, "")}`;
//   }
//   return undefined;
// }

// /** Given raw image value, return either a presigned URL (for 24-hex id) or legacy uploads URL */
// export function getImageSrc(raw: string | undefined, map: Record<string, string>): string | undefined {
//   if (is24Hex(raw)) return map[raw!];
//   return buildLegacyUrl(raw);
// }

export function buildLegacyUrl(raw?: string): string | undefined {
  if (!raw) return undefined;
  if (raw.includes("uploads")) {
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
    const clean = raw.replace(/\\/g, "/").replace(/^\//, "");
    return `${base}/${clean}`;
  }
  return undefined;
}

// ID -> presigned url OR legacy url
export function getImageSrc(raw: string | undefined, map: Record<string, string>): string | undefined {
  if (!raw) return undefined;
  if (is24Hex(raw)) return map[raw];
  return buildLegacyUrl(raw);
}

/** Cloud file id, uploads path, or absolute URL from common entity image fields. */
export function pickRawImageValue(item: Record<string, any> | null | undefined): string | undefined {
  if (!item || typeof item !== "object") return undefined;
  const candidates = [item.image, item.imageUrl, item.logoUrl, item.logo];
  for (const c of candidates) {
    if (!c || typeof c !== "string") continue;
    const s = c.trim();
    if (!s) continue;
    if (is24Hex(s)) return s;
    if (s.includes("uploads")) return s;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
  }
  return undefined;
}

export function resolvePresignedImageSrc(
  raw: string | undefined,
  map: Record<string, string>,
  fallback?: string,
): string | undefined {
  if (!raw) return fallback;
  const src = getImageSrc(raw, map);
  if (src) return src;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  return fallback;
}

export function normalizeRowImageField<T extends Record<string, any>>(item: T): T {
  const image = pickRawImageValue(item);
  return image ? { ...item, image } : item;
}

/** Renders presigned image, or a default placeholder image when none is available. */
export const EntityThumb: React.FC<{
  item: Record<string, any>;
  imageMap: Record<string, string>;
  textFallback?: React.ReactNode;
  fallbackImage?: string;
  className?: string;
  textClassName?: string;
}> = ({ item, imageMap, textFallback, fallbackImage, className = "entity-thumb", textClassName }) => {
  const [useFallback, setUseFallback] = React.useState(false);
  const raw = pickRawImageValue(item);
  const resolvedSrc = React.useMemo(() => {
    if (!raw) return undefined;
    return resolvePresignedImageSrc(raw, imageMap);
  }, [raw, imageMap]);
  const displaySrc = !useFallback && resolvedSrc ? resolvedSrc : fallbackImage;
  const baseClass = className.trim().split(/\s+/)[0] || "entity-thumb";
  return (
    <div
      className={`${className} ${displaySrc ? `${baseClass}--has-image` : `${baseClass}--empty`}`}
    >
      {displaySrc ? (
        <img
          src={displaySrc}
          alt=""
          onError={() => {
            if (fallbackImage && displaySrc !== fallbackImage) setUseFallback(true);
          }}
        />
      ) : (
        <span className={textClassName || `${baseClass}__text`}>{textFallback}</span>
      )}
    </div>
  );
};

/** Global, reusable hook to presign all ids in a list (with per-id fallback) */
export function usePresignedMap<T extends Record<string, any>>(
  data: T[] | undefined,
  field: keyof T | string,
  token: string | null | undefined
) {
  const [map, setMap] = useState<Record<string, string>>({});
  const idsKey = useMemo(
    () => collectCloudIds(data || [], field as keyof T).sort().join("\u0001"),
    [data, field],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const tok = (token || "").trim();
      const ids = idsKey ? idsKey.split("\u0001") : [];
      if (!ids.length || !tok) {
        if (!cancelled) setMap({});
        return;
      }
      try {
        // Batch presign only — missing/deleted file ids show placeholder UI (no per-id GET /work-drive/files/:id).
        const next = await batchPresignUrls(ids, tok);
        if (!cancelled) setMap(next);
      } catch {
        if (!cancelled) setMap({});
      }
    })();

    return () => { cancelled = true; };
  }, [idsKey, token]);

  return map;
}

/** Tiny, reusable table cell component */
export const TableImageCell: React.FC<{
  raw?: string;
  map: Record<string, string>;
  size?: number;
  className?: string;
  alt?: string;
}> = React.memo(({ raw, map, size = 40, className = "text-sm text-center S-No-2", alt = "Image" }) => {
  const src = useMemo(() => getImageSrc(raw, map), [raw, map]);
  const isId = is24Hex(raw);
  return (
    <div className={className}>
      {src ? (
        <img
          src={src}
          alt={alt}
          style={{ width: size, height: size, objectFit: "cover", borderRadius: 4 }}
        />
      ) : raw && isId ? (
        <span>Loading…</span>
      ) : (
        <span>No Image</span>
      )}
    </div>
  );
});

// export function getVariantImageIds(payload: { data?: any[] } | any[]): string[] {
//   const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data! : [];
//   const out: string[] = [];
//   for (const row of rows) {
//     const variants = Array.isArray(row?.product?.variants) ? row.product.variants : [];
//     console.log(variants,"variants")
//     for (const v of variants) {
//       const imgs = Array.isArray(v?.images) ? v.images : [];
//       console.log(imgs,"imgs")
//       for (const m of imgs) {
//         const id = m?.url;
//         if (is24Hex(id)) out.push(id);
//       }
//     }
//   }
//   return Array.from(new Set(out)); // de-dupe
// }



// export function useVariantImageUrlMap(
//   payload: { data?: any[] } | any[] | undefined,
//   token: string | null | undefined
// ) {
//   const [map, setMap] = React.useState<Record<string, string>>({});

//   // Compute a stable key for the current set of ids + token to avoid refetch loops
//   const { tok, ids, key } = React.useMemo(() => {
//     const tok = (token || "").trim();
//     const ids = payload ? getVariantImageIds(payload) : [];
//     const key = tok + "|" + ids.join(",");
//     return { tok, ids, key };
//   }, [payload, token]);

//   const lastKeyRef = React.useRef<string>("");

//   React.useEffect(() => {
//     let cancelled = false;

//     if (!tok || !ids.length) {
//       lastKeyRef.current = "";
//       setMap((prev) => (Object.keys(prev).length ? {} : prev));
//       return;
//     }

//     if (key === lastKeyRef.current) return; // no change
//     lastKeyRef.current = key;

//     (async () => {
//       try {
//         const m = await batchPresignUrls(ids, tok);
//         if (!cancelled) setMap(m);
//       } catch {
//         if (!cancelled) setMap({});
//       }
//     })();

//     return () => {
//       cancelled = true;
//     };
//   }, [key, tok, ids]);

//   return map;
// }




// Normalize / build legacy "uploads/..." URL (supports both / and \ from API)


/** Collect all variant image ids from payload.data[].product.variants[].images[].url */
// export function getVariantImageIds(payload: { data?: any[] } | any[]): string[] {
//   const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data! : [];
//   const out: string[] = [];
//   for (const row of rows) {
//     const variants = Array.isArray(row?.product?.variants) ? row.product.variants :row.rotational_product.images
// ;
//     for (const v of variants) {
//       const imgs = Array.isArray(v?.images) ? v.images : [];
//       for (const m of imgs) {
//         const id = m?.url;
//         if (is24Hex(id)) out.push(id);
//       }
//     }
//   }
//   return Array.from(new Set(out)); 
// }

export function getVariantImageIds(payload: { data?: any[] } | any[]): string[] {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data!
    : payload
    ? [payload]
    : [];

  const out: string[] = [];

  const pushIfHex = (val?: string) => {
    if (is24Hex(val)) out.push(val as string);
  };

  const collectImages = (arr: any[]) => {
    for (const m of arr) {
      if (!m) continue;
      if (typeof m === "string") {
        pushIfHex(m);
      } else {
        pushIfHex(m?.url);
        if (Array.isArray(m?.images)) collectImages(m.images);
      }
    }
  };

  const processProductLike = (product: any) => {
    if (!product) return;
    const imageField = product?.image;
    if (typeof imageField === "string") pushIfHex(imageField);
    if (typeof imageField?.url === "string") pushIfHex(imageField.url);

    if (Array.isArray(product?.images)) collectImages(product.images);

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    for (const v of variants) {
      collectImages(Array.isArray(v?.images) ? v.images : []);
    }

    if (Array.isArray(product?.rotational_product?.images)) {
      collectImages(product.rotational_product.images);
    }
  };

  for (const row of rows) {
    if (!row) continue;

    if (typeof row === "string") {
      pushIfHex(row);
      continue;
    }

    if (Array.isArray(row)) {
      collectImages(row);
      continue;
    }

    if (typeof row?.url === "string") pushIfHex(row.url);
    if (Array.isArray(row?.images)) collectImages(row.images);

    processProductLike(row);
    processProductLike(row?.product);
    processProductLike(row?.rotational_product);
  }

  return Array.from(new Set(out));
}



type UrlMap = Record<string, string>;


function shallowEqual(a: UrlMap, b: UrlMap) {
  const ak = Object.keys(a), bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of bk) if (a[k] !== b[k]) return false;
  return true;
}

export function useVariantImageUrlMap(
  payload: { data?: any[] } | any[] | any | undefined,
  token: string | null | undefined
) {
  // console.log("useVariantImageUrlMap called with payload:", payload, "and token:", token);
  const [map, setMap] = React.useState<UrlMap>({});
  const cacheRef = React.useRef<UrlMap>({});
  const seqRef = React.useRef(0);
  /** Presign only when this key or token changes — not when `payload` is a new object with the same ids. */
  const lastIdsKeyRef = React.useRef<string>("");
  const lastTokenRef = React.useRef<string>("");

  // normalize payload and compute stable ids
  const { tok, ids, idsKey } = React.useMemo(() => {
    const tok = (token || "").trim();

    // Accept: array, {data: array}, or single object
    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray((payload as any)?.data)
      ? (payload as any).data
      : payload
      ? [payload]
      : [];

    const collected = getVariantImageIds(rows); // MUST return 24-hex ids
    // unique + sorted for deterministic keys
    const ids = Array.from(new Set(collected)).sort();
    const idsKey = ids.join("|");
    return { tok, ids, idsKey };
  }, [payload, token]);

  // 1) Prefill from cache immediately on ids change (no network/flicker)
  React.useEffect(() => {
    if (!ids.length) return;
    const subset: UrlMap = {};
    for (const id of ids) {
      const u = cacheRef.current[id];
      if (u) subset[id] = u;
    }
    if (Object.keys(subset).length > 0) {
      setMap((prev) => (shallowEqual(prev, subset) ? prev : subset));
    }
  }, [idsKey]);

  // 2) Fetch presigned URLs, ignore stale responses
  React.useEffect(() => {
    if (!tok) return;

    if (!ids.length) {
      lastIdsKeyRef.current = "";
      lastTokenRef.current = tok;
      return;
    }

    if (lastIdsKeyRef.current === idsKey && lastTokenRef.current === tok) return;
    lastIdsKeyRef.current = idsKey;
    lastTokenRef.current = tok;

    const mySeq = ++seqRef.current;

    (async () => {
      let presigned: UrlMap = {};
      try {
        presigned = await batchPresignUrls(ids, tok);
      } catch {
        // keep whatever we had; do not blank the map
        return;
      }

      // If a newer request started, drop this one
      if (seqRef.current !== mySeq) return;

      // Merge into cache
      if (presigned && Object.keys(presigned).length) {
        Object.assign(cacheRef.current, presigned);
      }

      // Build the subset for the *current* ids
      const subset: UrlMap = {};
      for (const id of ids) {
        const u = cacheRef.current[id];
        if (u) subset[id] = u;
      }

      // Only publish if it improves the map; never replace with empty
      setMap((prev) => {
        if (Object.keys(subset).length === 0) return prev;
        return shallowEqual(prev, subset) ? prev : subset;
      });
    })();
  }, [tok, idsKey]);

  return map;
}








export function getVariantImageIdsInventory(payload: { data?: any[] } | any[]): string[] {
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data!
    : payload
    ? [payload]
    : [];

  const out: string[] = [];

  const pushIfHex = (val?: string) => {
    if (is24Hex(val)) out.push(val as string);
  };

  const collectImages = (arr: any[]) => {
    for (const m of arr) {
      if (!m) continue;
      if (typeof m === "string") {
        pushIfHex(m);
      } else {
        pushIfHex(m?.url);
        if (Array.isArray(m?.images)) collectImages(m.images);
      }
    }
  };

  const processProductLike = (product: any) => {
    if (!product) return;
    const imageField = product?.image;
    if (typeof imageField === "string") pushIfHex(imageField);
    if (typeof imageField?.url === "string") pushIfHex(imageField.url);

    if (Array.isArray(product?.images)) collectImages(product.images);

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    for (const v of variants) {
      collectImages(Array.isArray(v?.images) ? v.images : []);
    }

    if (Array.isArray(product?.rotational_product?.images)) {
      collectImages(product.rotational_product.images);
    }
  };

  for (const row of rows) {
    if (!row) continue;

    if (typeof row === "string") {
      pushIfHex(row);
      continue;
    }

    if (Array.isArray(row)) {
      collectImages(row);
      continue;
    }

    if (typeof row?.url === "string") pushIfHex(row.url);
    if (Array.isArray(row?.images)) collectImages(row.images);

    processProductLike(row);
    processProductLike(row?.product);
    processProductLike(row?.rotational_product);
  }

  return Array.from(new Set(out));
}

export function useVariantImageUrlMapinventory(
  payload: { data?: any[] } | any[] | any | undefined,
  token: string | null | undefined
) {
  // console.log("useVariantImageUrlMap called with payload:", payload, "and token:", token);
  const [map, setMap] = React.useState<UrlMap>({});
  const cacheRef = React.useRef<UrlMap>({});
  const seqRef = React.useRef(0);
  const lastIdsKeyRef = React.useRef<string>("");
  const lastTokenRef = React.useRef<string>("");

  // normalize payload and compute stable ids
  const { tok, ids, idsKey } = React.useMemo(() => {
    const tok = (token || "").trim();

    // Accept: array, {data: array}, or single object
    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray((payload as any)?.data)
      ? (payload as any).data
      : payload
      ? [payload]
      : [];

    const collected =  getVariantImageIdsInventory(rows); // MUST return 24-hex ids
    // unique + sorted for deterministic keys
    const ids = Array.from(new Set(collected)).sort();
    const idsKey = ids.join("|");
    return { tok, ids, idsKey };
  }, [payload, token]);

  // 1) Prefill from cache immediately on ids change (no network/flicker)
  React.useEffect(() => {
    if (!ids.length) return;
    const subset: UrlMap = {};
    for (const id of ids) {
      const u = cacheRef.current[id];
      if (u) subset[id] = u;
    }
    if (Object.keys(subset).length > 0) {
      setMap((prev) => (shallowEqual(prev, subset) ? prev : subset));
    }
  }, [idsKey]);

  // 2) Fetch presigned URLs, ignore stale responses
  React.useEffect(() => {
    if (!tok) return;

    if (!ids.length) {
      lastIdsKeyRef.current = "";
      lastTokenRef.current = tok;
      return;
    }

    if (lastIdsKeyRef.current === idsKey && lastTokenRef.current === tok) return;
    lastIdsKeyRef.current = idsKey;
    lastTokenRef.current = tok;

    const mySeq = ++seqRef.current;

    (async () => {
      let presigned: UrlMap = {};
      try {
        presigned = await batchPresignUrls(ids, tok);
      } catch {
        // keep whatever we had; do not blank the map
        return;
      }

      // If a newer request started, drop this one
      if (seqRef.current !== mySeq) return;

      // Merge into cache
      if (presigned && Object.keys(presigned).length) {
        Object.assign(cacheRef.current, presigned);
      }

      // Build the subset for the *current* ids
      const subset: UrlMap = {};
      for (const id of ids) {
        const u = cacheRef.current[id];
        if (u) subset[id] = u;
      }

      // Only publish if it improves the map; never replace with empty
      setMap((prev) => {
        if (Object.keys(subset).length === 0) return prev;
        return shallowEqual(prev, subset) ? prev : subset;
      });
    })();
  }, [tok, idsKey]);

  return map;
}
/**
 * Stable, flicker-free image URL map hook:
 * - Keeps previous map while a refresh is in-flight
 * - Re-fetches only when (token + id-set) actually changes
 * - Caches presigned results across updates
 */
// export function useVariantImageUrlMap(
//   payload: { data?: any[] } | any[] | undefined,
//   token: string | null | undefined
// ) {
//   const [map, setMap] = React.useState<Record<string, string>>({});
//   const cacheRef = React.useRef<Record<string, string>>({});
//   const lastKeyRef = React.useRef<string>("");

//   // Build stable key from token + sorted ids (so equal sets don't refetch)
//   const { tok, ids, key } = React.useMemo(() => {
//     const tok = (token || "").trim();
//     const ids = payload ? getVariantImageIds(payload) : [];
//     console.log(ids,"ids")
//     // sort for deterministic key, join for fast equality checks
//     const key = tok + "|" + ids.slice().sort().join(",");
//     return { tok, ids, key };
//   }, [payload, token]);

//   React.useEffect(() => {
//     let cancelled = false;

//     // Important: do NOT nuke previous map when ids are temporarily empty
//     // This prevents flicker during table refreshes.
//     if (!tok || !ids.length) return;

//     if (key === lastKeyRef.current) return; // nothing changed
//     lastKeyRef.current = key;

//     (async () => {
//       try {
//         const presigned = await batchPresignUrls(ids, tok);
//          console.log(presigned,"presigned123")
//         if (cancelled) return;
       

//         // Merge into cache and publish only the subset we need
//         cacheRef.current = { ...cacheRef.current, ...presigned };
//         const subset: Record<string, string> = {};
//         for (const id of ids) {
//           const u = cacheRef.current[id];
//           if (u) subset[id] = u;
//         }

//         // Avoid useless state updates
//         setMap((prev) => {
//           const prevKeys = Object.keys(prev);
//           const nextKeys = Object.keys(subset);
//           const same =
//             prevKeys.length === nextKeys.length &&
//             nextKeys.every((k) => prev[k] === subset[k]);
//           return same ? prev : subset;
//         });
//       } catch {
//         // Keep the old map on error as well (prevents flicker)
//       }
//     })();

//     return () => {
//       cancelled = true;
//     };
//   }, [key, tok, ids]);

//   console.log("map123", map);

//   return map;
// }
























// // ==== Reusable hook + helpers for table images ====
// // (Place in components/Imageshelper.ts next to your existing exports)

// import React, { useEffect, useMemo, useState } from "react";

// // If these are in this file already, you can remove these imports and use local defs.
// import { batchPresignUrls, collectCloudIds, getCloudFileById } from "./Imageshelper";

// // 24-hex Cloud Drive id?
// const is24Hex = (s?: string) => !!s && /^[0-9a-f]{24}$/i.test(s);

// // Build legacy URL when the row has old "uploads/..." relative path
// // export function buildLegacyUrl(raw?: string): string | undefined {
// //   if (typeof raw === "string" && raw.includes("uploads/")) {
// //     const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
// //     return `${base}/${raw.replace(/^\//, "")}`;
// //   }
// //   return undefined;
// // }

// // /** Given raw image value, return either a presigned URL (for 24-hex id) or legacy uploads URL */
// // export function getImageSrc(raw: string | undefined, map: Record<string, string>): string | undefined {
// //   if (is24Hex(raw)) return map[raw!];
// //   return buildLegacyUrl(raw);
// // }

// export function buildLegacyUrl(raw?: string): string | undefined {
//   if (!raw) return undefined;
//   if (raw.includes("uploads")) {
//     const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
//     const clean = raw.replace(/\\/g, "/").replace(/^\//, "");
//     return `${base}/${clean}`;
//   }
//   return undefined;
// }

// // ID -> presigned url OR legacy url
// export function getImageSrc(raw: string | undefined, map: Record<string, string>): string | undefined {
//   if (!raw) return undefined;
//   if (is24Hex(raw)) return map[raw];
//   return buildLegacyUrl(raw);
// }

// /** Global, reusable hook to presign all ids in a list (with per-id fallback) */
// export function usePresignedMap<T extends Record<string, any>>(
//   data: T[] | undefined,
//   field: keyof T | string,
//   token: string | null | undefined
// ) {
//   const [map, setMap] = useState<Record<string, string>>({});

//   useEffect(() => {
//     let cancelled = false;

//     (async () => {
//       const tok = (token || "").trim();
//       if (!data?.length || !tok) {
//         if (!cancelled) setMap({});
//         return;
//       }

//       const ids = collectCloudIds(data, field as keyof T);
//       if (!ids.length) {
//         if (!cancelled) setMap({});
//         return;
//       }
// console.log(ids,"ids123")
//       try {
//         // 1) batch presign
//         const primary = await batchPresignUrls(ids, tok);
//         let next = { ...primary };

//         // 2) fallback: GET per-id for any missing
//         const missing = ids.filter((id) => !next[id]);
//         if (missing.length) {
//           const results = await Promise.allSettled(missing.map((id) => getCloudFileById(id, tok)));
//           results.forEach((res, i) => {
//             if (res.status === "fulfilled" && res.value?.url) {
//               next[missing[i]] = res.value.url!;
//             }
//           });
//         }

//         if (!cancelled) setMap(next);
//       } catch {
//         if (!cancelled) setMap({});
//       }
//     })();

//     return () => { cancelled = true; };
//   }, [data, field, token]);

//   return map;
// }

// /** Tiny, reusable table cell component */
// export const TableImageCell: React.FC<{
//   raw?: string;
//   map: Record<string, string>;
//   size?: number;
//   className?: string;
//   alt?: string;
// }> = React.memo(({ raw, map, size = 40, className = "text-sm text-center S-No-2", alt = "Image" }) => {
//   const src = useMemo(() => getImageSrc(raw, map), [raw, map]);
//   const isId = is24Hex(raw);
//  console.log(src,"src")
//   return (
//     <div className={className}>
//       {src ? (
//         <img
//           src={src}
//           alt={alt}
//           style={{ width: size, height: size, objectFit: "cover", borderRadius: 4 }}
//         />
//       ) : raw && isId ? (
//         <span>Loading…</span>
//       ) : (
//         <span>No Image</span>
//       )}
//     </div>
//   );
// });

// // export function getVariantImageIds(payload: { data?: any[] } | any[]): string[] {
// //   const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data! : [];
// //   const out: string[] = [];
// //   for (const row of rows) {
// //     const variants = Array.isArray(row?.product?.variants) ? row.product.variants : [];
// //     console.log(variants,"variants")
// //     for (const v of variants) {
// //       const imgs = Array.isArray(v?.images) ? v.images : [];
// //       console.log(imgs,"imgs")
// //       for (const m of imgs) {
// //         const id = m?.url;
// //         if (is24Hex(id)) out.push(id);
// //       }
// //     }
// //   }
// //   return Array.from(new Set(out)); // de-dupe
// // }



// // export function useVariantImageUrlMap(
// //   payload: { data?: any[] } | any[] | undefined,
// //   token: string | null | undefined
// // ) {
// //   const [map, setMap] = React.useState<Record<string, string>>({});

// //   // Compute a stable key for the current set of ids + token to avoid refetch loops
// //   const { tok, ids, key } = React.useMemo(() => {
// //     const tok = (token || "").trim();
// //     const ids = payload ? getVariantImageIds(payload) : [];
// //     const key = tok + "|" + ids.join(",");
// //     return { tok, ids, key };
// //   }, [payload, token]);

// //   const lastKeyRef = React.useRef<string>("");

// //   React.useEffect(() => {
// //     let cancelled = false;

// //     if (!tok || !ids.length) {
// //       lastKeyRef.current = "";
// //       setMap((prev) => (Object.keys(prev).length ? {} : prev));
// //       return;
// //     }

// //     if (key === lastKeyRef.current) return; // no change
// //     lastKeyRef.current = key;

// //     (async () => {
// //       try {
// //         const m = await batchPresignUrls(ids, tok);
// //         if (!cancelled) setMap(m);
// //       } catch {
// //         if (!cancelled) setMap({});
// //       }
// //     })();

// //     return () => {
// //       cancelled = true;
// //     };
// //   }, [key, tok, ids]);

// //   return map;
// // }




// // Normalize / build legacy "uploads/..." URL (supports both / and \ from API)


// /** Collect all variant image ids from payload.data[].product.variants[].images[].url */
// // export function getVariantImageIds(payload: { data?: any[] } | any[]): string[] {
// //   const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data! : [];
// //   const out: string[] = [];
// //   for (const row of rows) {
// //     const variants = Array.isArray(row?.product?.variants) ? row.product.variants : [];
// //     for (const v of variants) {
// //       const imgs = Array.isArray(v?.images) ? v.images : [];
// //       for (const m of imgs) {
// //         const id = m?.url;
// //         if (is24Hex(id)) out.push(id);
// //       }
// //     }
// //   }
// //   return Array.from(new Set(out)); // de-dupe
// // }

// export function getVariantImageIds(payload: { data?: any[] } | any): string[] {
//   // Normalize to an array of rows
//   const rows = Array.isArray(payload)
//     ? payload
//     : Array.isArray(payload?.data)
//       ? payload.data!
//       : payload
//         ? [payload]
//         : [];

//   const out: string[] = [];

//   for (const row of rows) {
//     // Row might already be a product; otherwise use row.product
//     const product = row?.product || row?.rotational_product || row;

//     // Top-level product.image (single)
//     const heroId = product?.image?.url;
//     if (is24Hex(heroId)) out.push(heroId);

//     // Top-level product.images (array)
//     const prodImages = Array.isArray(product?.images) ? product.images : [];
//     console.log("prodImages",prodImages)
//     for (const m of prodImages) {
//       console.log(prodImages,"prodImages123")
//         console.log(m,"m")
//       const id = m?.url;
//       console.log(id,"id")
//       if (is24Hex(id)) out.push(id);
//     }

//     // ✅ Proper variants extraction
//     const variants = Array.isArray(product?.variants) ? product.variants : [];
//     for (const v of variants) {
//       const imgs = Array.isArray(v?.images) ? v.images : [];
//       for (const m of imgs) {
      
//         const id = m?.url;
//         if (is24Hex(id)) out.push(id);
//       }
//     }
//   }

//   // De-dupe
//   return Array.from(new Set(out));
// }


// type UrlMap = Record<string, string>;


// function shallowEqual(a: UrlMap, b: UrlMap) {
//   const ak = Object.keys(a), bk = Object.keys(b);
//   if (ak.length !== bk.length) return false;
//   for (const k of bk) if (a[k] !== b[k]) return false;
//   return true;
// }

// export function useVariantImageUrlMap(
//   payload: { data?: any[] } | any[] | any | undefined,
//   token: string | null | undefined
// ) {
//   const [map, setMap] = React.useState<UrlMap>({});
//   const cacheRef = React.useRef<UrlMap>({});
//   const seqRef = React.useRef(0);

//   // normalize payload and compute stable ids
//   const { tok, ids, idsKey } = React.useMemo(() => {
//     const tok = (token || "").trim();

//     // Accept: array, {data: array}, or single object
//     const rows = Array.isArray(payload)
//       ? payload
//       : Array.isArray((payload as any)?.data)
//       ? (payload as any).data
//       : payload
//       ? [payload]
//       : [];

//       console.log(rows,"rows")  

//     const collected = getVariantImageIds(rows); // MUST return 24-hex ids
//     console.log(collected,"collected")
//     // unique + sorted for deterministic keys
//     const ids = Array.from(new Set(collected)).sort();
//     const idsKey = ids.join("|");
//     return { tok, ids, idsKey };
//   }, [payload, token]);

//   // 1) Prefill from cache immediately on ids change (no network/flicker)
//   React.useEffect(() => {
//     if (!ids.length) return;
//     const subset: UrlMap = {};
//     for (const id of ids) {
//       const u = cacheRef.current[id];
//       if (u) subset[id] = u;
//     }
//     if (Object.keys(subset).length > 0) {
//       setMap((prev) => (shallowEqual(prev, subset) ? prev : subset));
//     }
//   }, [idsKey]);

//   // 2) Fetch presigned URLs, ignore stale responses
//   React.useEffect(() => {
//     if (!tok || !ids.length) return;

//     const mySeq = ++seqRef.current;

//     (async () => {
//       let presigned: UrlMap = {};
//       try {
//         presigned = await batchPresignUrls(ids, tok);
//       } catch {
//         // keep whatever we had; do not blank the map
//         return;
//       }

//       // If a newer request started, drop this one
//       if (seqRef.current !== mySeq) return;

//       // Merge into cache
//       if (presigned && Object.keys(presigned).length) {
//         Object.assign(cacheRef.current, presigned);
//       }

//       // Build the subset for the *current* ids
//       const subset: UrlMap = {};
//       for (const id of ids) {
//         const u = cacheRef.current[id];
//         if (u) subset[id] = u;
//       }

//       // Only publish if it improves the map; never replace with empty
//       setMap((prev) => {
//         if (Object.keys(subset).length === 0) return prev;
//         return shallowEqual(prev, subset) ? prev : subset;
//       });
//     })();
//   }, [tok, idsKey]);

//   return map;
// }
// /**
//  * Stable, flicker-free image URL map hook:
//  * - Keeps previous map while a refresh is in-flight
//  * - Re-fetches only when (token + id-set) actually changes
//  * - Caches presigned results across updates
//  */
// // export function useVariantImageUrlMap(
// //   payload: { data?: any[] } | any[] | undefined,
// //   token: string | null | undefined
// // ) {
// //   const [map, setMap] = React.useState<Record<string, string>>({});
// //   const cacheRef = React.useRef<Record<string, string>>({});
// //   const lastKeyRef = React.useRef<string>("");

// //   // Build stable key from token + sorted ids (so equal sets don't refetch)
// //   const { tok, ids, key } = React.useMemo(() => {
// //     const tok = (token || "").trim();
// //     const ids = payload ? getVariantImageIds(payload) : [];
// //     console.log(ids,"ids")
// //     // sort for deterministic key, join for fast equality checks
// //     const key = tok + "|" + ids.slice().sort().join(",");
// //     return { tok, ids, key };
// //   }, [payload, token]);

// //   React.useEffect(() => {
// //     let cancelled = false;

// //     // Important: do NOT nuke previous map when ids are temporarily empty
// //     // This prevents flicker during table refreshes.
// //     if (!tok || !ids.length) return;

// //     if (key === lastKeyRef.current) return; // nothing changed
// //     lastKeyRef.current = key;

// //     (async () => {
// //       try {
// //         const presigned = await batchPresignUrls(ids, tok);
// //          console.log(presigned,"presigned123")
// //         if (cancelled) return;
       

// //         // Merge into cache and publish only the subset we need
// //         cacheRef.current = { ...cacheRef.current, ...presigned };
// //         const subset: Record<string, string> = {};
// //         for (const id of ids) {
// //           const u = cacheRef.current[id];
// //           if (u) subset[id] = u;
// //         }

// //         // Avoid useless state updates
// //         setMap((prev) => {
// //           const prevKeys = Object.keys(prev);
// //           const nextKeys = Object.keys(subset);
// //           const same =
// //             prevKeys.length === nextKeys.length &&
// //             nextKeys.every((k) => prev[k] === subset[k]);
// //           return same ? prev : subset;
// //         });
// //       } catch {
// //         // Keep the old map on error as well (prevents flicker)
// //       }
// //     })();

// //     return () => {
// //       cancelled = true;
// //     };
// //   }, [key, tok, ids]);

// //   console.log("map123", map);

// //   return map;
// // }